import React from 'react';
import {
  Document,
  Page,
  Text,
  StyleSheet,
  Image,
  View
} from '@react-pdf/renderer';
import {Trans, useTranslation} from 'react-i18next';
import {IFluConsentFormState} from './FluConsentForm';
import {
  DONT_KNOW_HIGHLIGHT_COLOR,
  NO_HIGHLIGHT_COLOR
} from 'src/style/variable';

const HEADING_TEXT_SIZE = 24;
const BODY_TEXT_SIZE = 14;
const FONT_FAMILY = 'Times-Roman';
const PAGE_MARGIN = 12;
const SIGNATURE_MAX_HEIGHT = 50;
const TEXT_VERTICAL_MARGIN = 6;
const IMAGE_VERTICAL_MARGIN = 15;
const LIST_ITEM_LEFT_MARGIN = PAGE_MARGIN + 6;
const LIST_ITEM_VERTICAL_MARGIN = 4;

const styles = StyleSheet.create({
  body: {
    paddingTop: 35,
    paddingBottom: 65,
    paddingHorizontal: 35
  },
  title: {
    fontSize: HEADING_TEXT_SIZE,
    fontFamily: FONT_FAMILY,
    margin: PAGE_MARGIN
  },
  subtitle: {
    fontSize: 18,
    margin: PAGE_MARGIN,
    fontFamily: FONT_FAMILY
  },
  text: {
    margin: PAGE_MARGIN,
    marginBottom: TEXT_VERTICAL_MARGIN,
    marginTop: TEXT_VERTICAL_MARGIN,
    fontSize: BODY_TEXT_SIZE,
    textAlign: 'justify',
    fontFamily: FONT_FAMILY
  },
  image: {
    marginVertical: IMAGE_VERTICAL_MARGIN,
    marginHorizontal: PAGE_MARGIN,
    maxHeight: SIGNATURE_MAX_HEIGHT,
    marginRight: 'auto'
  },
  highlightedUnderlinedText: {
    fontWeight: 'bold',
    textDecoration: 'underline'
  },
  highlightedText: {
    fontWeight: 'bold'
  },
  listItem: {
    marginRight: PAGE_MARGIN,
    marginLeft: LIST_ITEM_LEFT_MARGIN,
    marginBottom: LIST_ITEM_VERTICAL_MARGIN,
    marginTop: LIST_ITEM_VERTICAL_MARGIN,
    fontSize: BODY_TEXT_SIZE,
    textAlign: 'justify',
    fontFamily: FONT_FAMILY
  },
  questionWrapper: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  question: {
    flex: 1,
    marginRight: 20
  },
  answer: {
    width: 120
  },
  highlightYes: {
    color: NO_HIGHLIGHT_COLOR
  },
  highlightDontKnow: {
    color: DONT_KNOW_HIGHLIGHT_COLOR
  }
});
const FluConsentFormDoc = ({
  date,
  time,
  digitalSignatureURL,
  hasAllergyToIngredient,
  hasGuillianSyndrome,
  isSickToday,
  hasReactionToInfulenza,
  formCompletedBy,
  patientDOB,
  patientName
}: {
  date: string;
  time: string;
  patientName: string;
  patientDOB: string;
} & IFluConsentFormState): JSX.Element => {
  const {t} = useTranslation();

  const questionAndAnswers = [
    {
      question: t(
        'APPOINTMENTS_SCHEDULAR_PORTAL.FLU_CONSENT_FORM.QUESTIONS.QUESTION_1'
      ),
      answer: isSickToday
    },
    {
      question: t(
        'APPOINTMENTS_SCHEDULAR_PORTAL.FLU_CONSENT_FORM.QUESTIONS.QUESTION_2'
      ),
      answer: hasAllergyToIngredient
    },
    {
      question: t(
        'APPOINTMENTS_SCHEDULAR_PORTAL.FLU_CONSENT_FORM.QUESTIONS.QUESTION_3'
      ),
      answer: hasReactionToInfulenza
    },
    {
      question: t(
        'APPOINTMENTS_SCHEDULAR_PORTAL.FLU_CONSENT_FORM.QUESTIONS.QUESTION_4'
      ),
      answer: hasGuillianSyndrome
    }
  ];

  const getAnswerStyle = (answer: string | undefined) => {
    if (answer === 'Yes') {
      return styles.highlightYes;
    } else if (answer === "Don't Know") {
      return styles.highlightDontKnow;
    }
    return {};
  };
  return (
    <Document>
      <Page style={styles.body}>
        <Text style={styles.title}>
          {t('APPOINTMENTS_SCHEDULAR_PORTAL.FLU_CONSENT_FORM.TITLE')}
        </Text>
        <Text style={styles.text}>
          <Trans
            i18nKey={'APPOINTMENTS_SCHEDULAR_PORTAL.FLU_CONSENT_FORM.INFO'}
            components={[<Text style={styles.highlightedText} key="X" />]}
          />
        </Text>
        <View>
          {questionAndAnswers.map(item => (
            <View key={item.question} style={styles.questionWrapper}>
              <Text style={{...styles.text, ...styles.question}}>
                {item.question}
              </Text>
              <Text
                style={[
                  styles.text,
                  styles.answer,
                  getAnswerStyle(item.answer)
                ]}>
                {item.answer}
              </Text>
            </View>
          ))}
        </View>
        <Text style={styles.text}>
          {t(
            'APPOINTMENTS_SCHEDULAR_PORTAL.FLU_CONSENT_FORM.FORM_COMPLETED_BY'
          )}
          : {formCompletedBy}
        </Text>
        <Text style={styles.text}>
          {t('APPOINTMENTS_SCHEDULAR_PORTAL.FLU_CONSENT_FORM.PATIENT_NAME')}:{' '}
          {patientName}
        </Text>
        <Text style={styles.text}>
          {t('APPOINTMENTS_SCHEDULAR_PORTAL.FLU_CONSENT_FORM.PATIENT_DOB')}:{' '}
          {patientDOB}
        </Text>
        {/* eslint-disable-next-line react/jsx-tag-spacing */}
        {digitalSignatureURL ? (
          <Image style={styles.image} src={digitalSignatureURL} />
        ) : (
          <View />
        )}
        <Text style={styles.text}>
          {t('APPOINTMENTS_SCHEDULAR_PORTAL.FLU_CONSENT_FORM.DATE')}: {date}
        </Text>
        <Text style={styles.text}>
          {t('APPOINTMENTS_SCHEDULAR_PORTAL.FLU_CONSENT_FORM.TIME')}: {time}
        </Text>
      </Page>
    </Document>
  );
};
export default FluConsentFormDoc;
